import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl, pageDataUrlWithSuffix} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {usePageDataContext} from '@github-ui/pull-request-page-data-tooling/page-data-context'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'
import {MergeError} from '../../helpers/merge-error'
import {sleep} from '../../helpers/sleep'
import type {PullRequestMergeMethod} from '../../types'

/**
 * Kicks off an async merge for the current pull request via `enqueue_stack`, then
 * polls `merge_request_status` until the request reaches a terminal state.
 *
 * @example
 * ```ts
 * const {mutate, isPending} = useAsyncMergeRequest({
 *   onError: error => console.log(error.message),
 *   onSuccess: () => console.log('merged'),
 * })
 *
 * mutate({mergeMethod: 'MERGE', commitTitle: 'My title', commitMessage: 'My message', authorEmail: 'me@example.com'})
 * ```
 */
type MutationData = {
  mergeMethod: PullRequestMergeMethod
  commitTitle?: string
  commitMessage?: string
  authorEmail?: string
  bypassRules?: boolean
}

type EnqueueAsyncMergeResponse =
  | {status: 'pending'; message: string; uuid: string}
  | {status: 'merged'; message: string; sha: string}
  | {status: 'enqueued'; message: string}
  | {status: 'failed'; error: string}

type MergeRequestStatusResponse =
  | {status: 'pending'; message: string}
  | {status: 'enqueued'; message: string}
  | {status: 'merged'; message: string; sha: string}
  | {status: 'failed'; error: string}

const POLL_INITIAL_DELAY = 1000
const POLL_BACKOFF_MULTIPLIER = 1.5
const POLL_TIMEOUT = 600_000

async function pollForTerminalState(
  fetchStatus: () => Promise<MergeRequestStatusResponse>,
  initialDelay = POLL_INITIAL_DELAY,
): Promise<MergeRequestStatusResponse> {
  const startTime = Date.now()
  let delay = initialDelay

  for (;;) {
    const status = await fetchStatus()
    if (status.status === 'merged' || status.status === 'enqueued' || status.status === 'failed') {
      return status
    }

    if (Date.now() - startTime + delay > POLL_TIMEOUT) {
      throw new Error('Async merge polling timed out')
    }

    await sleep(delay)
    delay *= POLL_BACKOFF_MULTIPLIER
  }
}

export function useAsyncMergeRequest({onError, onSuccess}: {onError: (error: Error) => void; onSuccess?: () => void}) {
  const enqueueApiURL = usePageDataUrl(PageData.enqueueStack)
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()
  const {basePageDataUrl} = usePageDataContext()

  const fetchStatus = async (uuid: string): Promise<MergeRequestStatusResponse> => {
    const statusApiURL = pageDataUrlWithSuffix(basePageDataUrl, PageData.mergeRequestStatus, encodeURIComponent(uuid))
    const response = await fetchWithErrorHandling(statusApiURL)
    const json = await parseJSONWithBetterErrors(response)
    throwErrorsIfBadResponse(
      response,
      json,
      new MergeError(json?.error || 'Unknown error occurred', [], response.status),
    )
    return json
  }

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const response = await fetchWithErrorHandling(enqueueApiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {...data, bypassRules: data.bypassRules ? 'true' : 'false'},
      })
      const json = await parseJSONWithBetterErrors(response)

      // A 409 conflict (an async request already exists for this pull request) still
      // returns a usable `status`/`uuid` pair. Only treat responses without a
      // recognizable `status` field as hard errors.
      if (!json?.status) {
        const predefinedError = new MergeError(
          json?.error || 'Unknown error occurred',
          json?.metadata?.ruleErrors || [],
          response.status,
        )
        throwErrorsIfBadResponse(response, json, predefinedError)
      }

      const enqueueResult = json as EnqueueAsyncMergeResponse
      if (enqueueResult.status === 'failed') {
        throw new MergeError(enqueueResult.error, [], response.status)
      }
      if (enqueueResult.status === 'merged' || enqueueResult.status === 'enqueued') {
        return enqueueResult
      }

      const terminalState = await pollForTerminalState(() => fetchStatus(enqueueResult.uuid))
      if (terminalState.status === 'failed') {
        throw new MergeError(terminalState.error, [], undefined)
      }
      return terminalState
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
      return onSuccess?.()
    },
    onError: (e: Error) => onError(e),
  })
}
